import { IconProp } from "@fortawesome/fontawesome-svg-core";

import {
  IApiReportRow,
  IMergedReportCategory,
  IMergedReportEntry,
  IReportCatalogEntry,
  IReportCategoryDefinition,
  TReportAdminScope,
} from "./reportCatalog.types";
import { hasSpecificReportPermission, isClientScopedReportVisible } from "./reportPermissions";
import { hasReportExport } from "./quickExport/quickExportConfig";
import { IUserPermissions } from "redux/reducers/models/user";

interface IBuildReportListCategoriesOptions {
  apiReports: IApiReportRow[];
  catalog: IReportCategoryDefinition[];
  scope: TReportAdminScope;
  permissions: IUserPermissions | Record<string, unknown> | undefined;
  dedicatedRouteCodes: ReadonlySet<string>;
  userClientId?: number;
}

interface ICatalogLookups {
  reportByCode: Map<string, IReportCatalogEntry>;
  categoryByNormalizedTitle: Map<string, { category: IReportCategoryDefinition; order: number }>;
}

const defaultCategoryIcon: IconProp = ["far", "folder"];

function categoryId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function normalizeCategoryName(name: string): string {
  return name.trim().toLowerCase();
}

function buildCatalogLookups(catalog: IReportCategoryDefinition[]): ICatalogLookups {
  const reportByCode = new Map<string, IReportCatalogEntry>();
  const categoryByNormalizedTitle = new Map<string, { category: IReportCategoryDefinition; order: number }>();

  catalog.forEach((category, index) => {
    categoryByNormalizedTitle.set(normalizeCategoryName(category.title), { category, order: index });

    category.reports.forEach(report => {
      reportByCode.set(report.code, report);
    });
  });

  return { reportByCode, categoryByNormalizedTitle };
}

function buildReportUrl(apiReport: IApiReportRow, dedicatedRouteCodes: ReadonlySet<string>): string {
  if (dedicatedRouteCodes.has(apiReport.code)) {
    return `/admin/report/${apiReport.code}`;
  }

  return `/admin/report/${apiReport.id}`;
}

function toReportEntry(
  apiReport: IApiReportRow,
  dedicatedRouteCodes: ReadonlySet<string>,
  catalogEntry: IReportCatalogEntry | undefined,
  hasPermission: boolean,
): IMergedReportEntry | null {
  if (catalogEntry?.enabled === false) {
    return null;
  }

  return {
    code: apiReport.code,
    title: catalogEntry?.title ?? apiReport.title,
    description: catalogEntry?.description ?? "",
    id: apiReport.id,
    url: buildReportUrl(apiReport, dedicatedRouteCodes),
    hasExport: hasReportExport(apiReport.code),
    hasPermission,
  };
}

function getCategoryMeta(
  categoryName: string,
  lookups: ICatalogLookups,
): Pick<IMergedReportCategory, "id" | "summary" | "icon"> {
  const catalogCategory = lookups.categoryByNormalizedTitle.get(normalizeCategoryName(categoryName))?.category;

  if (catalogCategory) {
    return {
      id: catalogCategory.id,
      summary: catalogCategory.summary,
      icon: catalogCategory.icon,
    };
  }

  return {
    id: categoryId(categoryName),
    summary: "",
    icon: defaultCategoryIcon,
  };
}

function sortCategoryNames(
  categoryNames: string[],
  lookups: ICatalogLookups,
): string[] {
  return [...categoryNames].sort((left, right) => {
    const leftOrder =
      lookups.categoryByNormalizedTitle.get(normalizeCategoryName(left))?.order ?? Number.MAX_SAFE_INTEGER;
    const rightOrder =
      lookups.categoryByNormalizedTitle.get(normalizeCategoryName(right))?.order ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return left.localeCompare(right);
  });
}

export function buildReportListCategories(options: IBuildReportListCategoriesOptions): IMergedReportCategory[] {
  const { apiReports, catalog, scope, permissions, dedicatedRouteCodes, userClientId } = options;
  const lookups = buildCatalogLookups(catalog);

  const categoryNames = new Set<string>();
  const reportsByCategory = new Map<string, IMergedReportEntry[]>();

  apiReports.forEach(apiReport => {
    if (!isClientScopedReportVisible(apiReport, userClientId, permissions, apiReport.code, scope)) {
      return;
    }

    const catalogEntry = lookups.reportByCode.get(apiReport.code);
    const categoryName = apiReport.category?.trim() || "Other";
    const hasPermission = hasSpecificReportPermission(permissions, apiReport.code, scope);
    const mergedEntry = toReportEntry(apiReport, dedicatedRouteCodes, catalogEntry, hasPermission);

    if (!mergedEntry) {
      return;
    }

    categoryNames.add(categoryName);

    if (!reportsByCategory.has(categoryName)) {
      reportsByCategory.set(categoryName, []);
    }

    reportsByCategory.get(categoryName)?.push(mergedEntry);
  });

  return sortCategoryNames([...categoryNames], lookups).map(categoryName => {
    const reports = (reportsByCategory.get(categoryName) ?? []).sort((left, right) =>
      left.title.localeCompare(right.title),
    );
    const meta = getCategoryMeta(categoryName, lookups);

    return {
      id: meta.id,
      title: categoryName,
      summary: meta.summary,
      icon: meta.icon,
      reports,
    };
  });
}

function matchesReportSearch(entry: IMergedReportEntry, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  return (
    entry.title.toLowerCase().includes(normalized) ||
    entry.code.toLowerCase().includes(normalized) ||
    entry.description.toLowerCase().includes(normalized)
  );
}

function isCategoryTitleSearchMatch(category: IMergedReportCategory, query: string): boolean {
  if (!query.trim()) {
    return false;
  }

  const normalized = query.trim().toLowerCase();
  return (
    category.title.toLowerCase().includes(normalized) ||
    category.id.toLowerCase().includes(normalized)
  );
}

export function applySearchToCategory(
  category: IMergedReportCategory,
  query: string,
): IMergedReportCategory {
  if (!query.trim()) {
    return category;
  }

  if (isCategoryTitleSearchMatch(category, query)) {
    return category;
  }

  return {
    ...category,
    reports: category.reports.filter(report => matchesReportSearch(report, query)),
  };
}

export function categoryHasSearchMatches(category: IMergedReportCategory, query: string): boolean {
  if (!query.trim()) {
    return true;
  }

  return applySearchToCategory(category, query).reports.length > 0;
}

export function flattenReports(categories: IMergedReportCategory[]): IMergedReportEntry[] {
  const reports: IMergedReportEntry[] = [];

  categories.forEach(category => {
    category.reports.forEach(report => {
      reports.push(report);
    });
  });

  return reports;
}
